import { Disclosure, Transition } from '@headlessui/react';
import omit from 'lodash/omit';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { Filters } from '@/modules/admin-dashboard/pages/AdminEntities.tsx';
import { useAdminRegisterListQuery } from '@/modules/admin-dashboard/queries/registers.tsx';
import { Select, SelectPaginate } from 'ui';
import { fixNameEnum } from 'ui/utils';
import { EntityType, RegisterType } from 'api';

const registerTypeOptions = Object.values(RegisterType).map(type => ({
  value: type as RegisterType,
  label: fixNameEnum(type),
}));
const entityTypeOptions = Object.values(EntityType).map(type => ({
  value: type as EntityType,
  label: fixNameEnum(type),
}));

interface EntitiesFiltersProps {
  filters: Filters;
  setFilters: Dispatch<SetStateAction<Filters>>;
}

const EntitiesFilters = ({ filters, setFilters }: EntitiesFiltersProps) => {
  const { fetchNextPage: fetchNextRegisterListPage } = useAdminRegisterListQuery({
    page: 1,
    page_size: 15,
  });

  const handleAsyncRegister = useCallback(
    async (page: number, search?: string) => {
      const res = await fetchNextRegisterListPage({ pageParam: { page, search } });
      const registers = res?.data?.pages?.at(-1)?.data || [];

      return registers.map(({ id, name }) => ({ label: name, value: id }));
    },
    [fetchNextRegisterListPage],
  );

  const handleChangeFilter = useCallback(
    (field: keyof Filters, value: string | number | null) => {
      setFilters(prev => {
        return value ? { ...prev, [field]: value } : omit(prev, field);
      });
    },
    [setFilters],
  );
  return (
    <div className="flex justify-end">
      <Transition
        enter="transition duration-100 ease-out"
        enterFrom="transform scale-95 opacity-0"
        enterTo="transform scale-100 opacity-100"
        leave="transition duration-75 ease-out"
        leaveFrom="transform scale-100 opacity-100"
        leaveTo="transform scale-95 opacity-0"
      >
        <Disclosure.Panel static>
          <div className="flex flex-wrap gap-2 items-end">
            <Select
              isClearable
              label=""
              placeholder="Holder type"
              className="flex-1 min-w-[130px]"
              options={entityTypeOptions}
              value={filters.type}
              onChange={handleChangeFilter.bind(null, 'type')}
            />
            <Select
              isClearable
              label=""
              placeholder="Register type"
              className="flex-1 min-w-[130px]"
              options={registerTypeOptions}
              value={filters.register_type}
              onChange={handleChangeFilter.bind(null, 'register_type')}
            />
            <SelectPaginate
              label=""
              isClearable
              onAsync={handleAsyncRegister}
              onChange={option =>
                handleChangeFilter('apply_register_id', option ? option.value : option)
              }
              placeholder="Register"
              className="flex-1 min-w-[200px]"
            />
          </div>
        </Disclosure.Panel>
      </Transition>
    </div>
  );
};

export default EntitiesFilters;
